import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEnrolledCourses } from '../api/educentialApi';
import { BookOpen, Target, CheckCircle, Clock, TrendingUp, Award } from 'lucide-react';

export default function LearningProgress() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLearningProgress();
  }, []);

  const loadLearningProgress = async () => {
    try {
      setLoading(true);
      const response = await getEnrolledCourses();
      if (response.success) {
        setEnrollments(response.data);
      }
    } catch (err) {
      console.error('Failed to load learning progress:', err);
      setError('Unable to load your learning progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-xl">!</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Progress</h3>
          <p className="text-gray-600 text-sm mb-6">{error}</p>
          <button
            onClick={loadLearningProgress}
            className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const stats = {
    total: enrollments.length,
    completed: enrollments.filter(e => e.progress === 100).length,
    inProgress: enrollments.filter(e => e.progress > 0 && e.progress < 100).length,
    notStarted: enrollments.filter(e => e.progress === 0).length,
    totalHours: enrollments.reduce((sum, e) => sum + (e.Course?.duration || 0), 0),
    averageProgress: enrollments.length > 0
      ? Math.round(enrollments.reduce((sum, e) => sum + e.progress, 0) / enrollments.length)
      : 0
  };

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Learning Progress</h1>
            <p className="text-gray-500 text-sm mt-1">
              Track your learning journey and achievements
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatCard
            icon={BookOpen}
            label="Total Courses"
            value={stats.total}
            color="blue"
          />
          <StatCard
            icon={CheckCircle}
            label="Completed"
            value={stats.completed}
            color="green"
          />
          <StatCard
            icon={Target}
            label="In Progress"
            value={stats.inProgress}
            color="yellow"
          />
          <StatCard
            icon={Clock}
            label="Total Hours"
            value={stats.totalHours}
            color="teal"
          />
          <StatCard
            icon={TrendingUp}
            label="Average Progress"
            value={`${stats.averageProgress}%`}
            color="indigo"
          />
          <StatCard
            icon={Award}
            label="Not Started"
            value={stats.notStarted}
            color="gray"
          />
        </div>

        {/* Overall Progress Bar */}
        {stats.total > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Progress</h2>
            <div className="relative">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block text-yellow-600">
                    {stats.completed} of {stats.total} courses completed
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-yellow-600">
                    {stats.averageProgress}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-lg bg-gray-200">
                <div
                  style={{ width: `${stats.averageProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500 transition-all duration-500"
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Courses List */}
        {enrollments.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Courses Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Start your learning journey by enrolling in courses from our catalog.
              </p>
              <Link
                to="/courses/catalog"
                className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors"
              >
                Browse Courses
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Course Progress</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {enrollments.map((enrollment) => (
                <CourseProgressItem
                  key={enrollment.id}
                  enrollment={enrollment}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// Course Progress Item Component
function CourseProgressItem({ enrollment }) {
  const course = enrollment.Course;
  const progressColor =
    enrollment.progress === 100 ? 'bg-green-500' :
    enrollment.progress > 50 ? 'bg-yellow-500' :
    enrollment.progress > 0 ? 'bg-blue-500' :
    'bg-gray-300';

  const statusBadge =
    enrollment.progress === 100 ? (
      <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs font-semibold">
        <CheckCircle className="w-3.5 h-3.5" />
        <span>Completed</span>
      </span>
    ) : enrollment.progress > 0 ? (
      <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs font-semibold">
        <Clock className="w-3.5 h-3.5" />
        <span>In Progress</span>
      </span>
    ) : (
      <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-xs font-semibold">
        <BookOpen className="w-3.5 h-3.5" />
        <span>Not Started</span>
      </span>
    );

  return (
    <Link
      to={`/courses/${course.id}/learn`}
      className="block px-6 py-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1 min-w-0 mr-4">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {course.title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {course.category} • {course.level}
            {course.duration && ` • ${course.duration} hours`}
          </p>
        </div>
        {statusBadge}
      </div>
      <div className="relative">
        <div className="flex mb-1 items-center justify-between">
          <span className="text-xs font-medium text-gray-600">
            Progress
          </span>
          <span className="text-xs font-semibold text-gray-900">
            {enrollment.progress}%
          </span>
        </div>
        <div className="overflow-hidden h-2 text-xs flex rounded-full bg-gray-200">
          <div
            style={{ width: `${enrollment.progress}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${progressColor} transition-all duration-300`}
          ></div>
        </div>
      </div>
      {enrollment.lastAccessedAt && (
        <p className="text-xs text-gray-500 mt-2">
          Last accessed: {new Date(enrollment.lastAccessedAt).toLocaleDateString()}
        </p>
      )}
    </Link>
  );
}
